import { JSX } from 'react';
import { Checkbox, CheckboxGroup } from '@ds';
import { Controller, useFormContext } from 'react-hook-form';
import { MdOutlineMap } from 'react-icons/md';
import { CSAtHubDaysFormSlice } from '@lp/features/Scheduler/SchedulerClinicalSpecialistsSettings/types';
import { Hub, Region } from '@/api/apis/resources/schedule';
import { WEEKDAYS } from '@/constants/date.constants';

type ClinicalSpecialistsAtHubFormPropTypes = {
  isReadOnly: boolean;
  regions: Region[];
};

export const CSAtHubFormSection = ({ regions, isReadOnly }: ClinicalSpecialistsAtHubFormPropTypes) => {
  const { control } = useFormContext<CSAtHubDaysFormSlice>();

  const renderTableCells = (hub: Hub) => {
    const tableCells: JSX.Element[] = [
      <td key={hub.id} className="min-w-50 pl-3">
        <span className="text-m-r ml-3 block">{hub.name}</span>
      </td>,
    ];

    const daysCells = (
      <td colSpan={6} key={`cellAtHubDays.${hub.id}`}>
        <Controller
          name={`atHubDays.${hub.id}`}
          control={control}
          render={({ field, fieldState: { invalid, error } }) => (
            <CheckboxGroup
              orientation="horizontal"
              isInvalid={invalid}
              errorMessage={error?.message}
              {...field}
              isDisabled={isReadOnly}
              key={`atHubDays.${hub.id}`}
            >
              {WEEKDAYS.map((day) => (
                <Checkbox
                  size="sm"
                  value={day}
                  classNames={{
                    base: 'px-2',
                  }}
                  key={day}
                >
                  {day.substring(0, 3)}
                </Checkbox>
              ))}
            </CheckboxGroup>
          )}
        />
      </td>
    );

    return [...tableCells, daysCells];
  };

  const renderTableRows = () => {
    let tableRows: JSX.Element[] = [
      <tr className="h-7 text-s-r text-base-25 [&>td]:px-3" key="row0">
        <td>Hubs</td>
        <td aria-colspan={7} colSpan={7}>
          Apply for days:
        </td>
      </tr>,
    ];

    regions.forEach((region: Region) => {
      tableRows.push(
        <tr key={`row${region.id}`} className="h-12">
          <td key={`col${region.id}`} className="p-3 bg-default-100 rounded-l-lg rounded-r-lg" colSpan={7}>
            <span className="text-sm font-bold flex">
              <MdOutlineMap size={24} className="mr-2" />
              {region.name}
            </span>
          </td>
        </tr>,
      );

      region.hubs.forEach((hub) => {
        tableRows = [
          ...tableRows,
          <tr key={`hubRow${hub.id}`} className="h-12">
            {renderTableCells(hub)}
          </tr>,
        ];
      });
    });

    return tableRows;
  };

  return (
    <div>
      <h2 className="text-hm text-[#111] mb-3">Clinical Specialists At Hub Weekly</h2>
      <table
        aria-label="Schedule table"
        className="relative border-separate border-spacing-0 px-4 py-4 bg-white rounded-lg w-full"
      >
        <thead className="bg-default-100">
          <tr>
            <th
              colSpan={8}
              className="text-left text-foreground-500 text-tiny font-semibold rounded-l-lg rounded-r-lg px-3 py-3"
            >
              AT HUB
            </th>
          </tr>
        </thead>
        <tbody>{renderTableRows()}</tbody>
      </table>
    </div>
  );
};
